'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useUser from '@alltrails/context/hooks/useUser';
import Button from '@alltrails/denali/components/Button';
import Car from '@alltrails/denali/icons/Car';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import { getTrailDirectionsUrl } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getTrailPageType from '@/utils/getTrailPageType';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const InstallMiniVariantInterstitial = dynamic(() => import('@alltrails/install-prompt/components/InstallMiniVariantInterstitial'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
};

export default function DirectionsButton({ trailDetails }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useInstallPrompt('contextual-install-prompt', false);
  const user = useUser();

  const experiments = useExperiments();
  const enableInstallPromptTreatment = experiments['web-growth-rm-app-install-prompt-directions']?.value === 'treatment';
  const sendInstallPromptExposure = useExposureEvent('web-growth-rm-app-install-prompt-directions');

  const latitude = trailDetails.location?.latitude;
  const longitude = trailDetails.location?.longitude;

  const url = getTrailDirectionsUrl(latitude, longitude);

  return (
    <>
      <Button
        disabled={!latitude || !longitude}
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'get directions',
            trail_page_location: 'mobile view',
            trail_page_type: getTrailPageType(trailDetails)
          });

          if (!user) {
            setIsOpen(true);
            return;
          }

          sendInstallPromptExposure();

          if (enableInstallPromptTreatment) {
            const didShowInstallPrompt = setShowInstallPrompt(true);
            if (didShowInstallPrompt) {
              return;
            }
          }

          window.open(url, '_blank', 'noopener noreferrer');
        }}
        text={<FormattedMessage defaultMessage="Get directions" />}
        width="full"
      />
      {isOpen && (
        <SignUpModal
          closeModal={() => setIsOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isOpen}
          onSuccess={() => {
            setIsOpen(false);
            window.open(url, '_blank', 'noopener noreferrer');
          }}
          trigger="Trail Detail Directions"
          upsellTrigger="Trail Detail Directions"
        />
      )}
      {showInstallPrompt && (
        <InstallMiniVariantInterstitial
          deeplinkData={{ pageType: 'navigate', pageId: trailDetails.id }}
          isOpen={showInstallPrompt}
          onRequestClose={() => setShowInstallPrompt(false)}
          promptLocation="trail page"
          displayTrigger="get directions"
          variant="trail-map"
          trailDetails={trailDetails}
          heading={<FormattedMessage defaultMessage="Take the trail with you" />}
          description={<FormattedMessage defaultMessage="Directions, offline maps, and navigation—all in the app, from start to finish." />}
          buttonText={<FormattedMessage defaultMessage="Open in app" />}
          shouldHideCloseButton
          clickOutsideDeactivates={false}
          secondaryAction={{
            icon: { Component: Car },
            text: <FormattedMessage defaultMessage="Get directions" />,
            onClick: () => {
              setShowInstallPrompt(false);
              window.open(url, '_blank', 'noopener noreferrer');
            }
          }}
        />
      )}
    </>
  );
}
